import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';
import { OverlayTrigger } from 'react-bootstrap';

import Spinner from '../Spinner';
import Tooltip from '../Tooltip';

import styles from './_index.module.scss';

interface ButtonV2Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  isLoading?: boolean;
  minWidth?: boolean;
  shape?: 'round' | 'square';
  size?: 'small' | 'medium' | 'large';
  tooltip?: React.ReactNode;
  variant?: 'default' | 'error' | 'primary' | 'subtle';
}

const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      children,
      className,
      disabled,
      icon,
      isLoading,
      minWidth,
      onClick,
      shape = 'square',
      size = 'medium',
      tooltip,
      variant = 'default',
      ...props
    }: ButtonV2Props,
    parentRef
  ) => {
    const localRef = useRef(null);
    const ref = parentRef || localRef;

    const buttonStyles = [
      styles[`button-${variant}`],
      styles[`button-shape-${shape}`],
      styles[`button-size-${size}`],
      { [styles['button-min-width']]: minWidth },
      { [styles['is-loading']]: isLoading },
    ];

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <OverlayTrigger
        overlay={disabled && tooltip ? <Tooltip>{tooltip}</Tooltip> : <></>}
        placement="bottom"
      >
        <button
          className={classNames(...buttonStyles, className)}
          disabled={disabled}
          onClick={handleClick}
          ref={ref}
          {...props}
        >
          {icon && <span className={styles['button-icon']}>{icon}</span>}
          <span className={styles['button-children']}>{children}</span>
          <Spinner className={styles['button-spinner']} />
        </button>
      </OverlayTrigger>
    );
  }
);

export default ButtonV2;
